import React from 'react'
import {
    Button,
    CircularProgress,
    DialogActions,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Typography,
    makeStyles
} from '@material-ui/core'
import { SwapHorizOutlined as MoveIcon } from '@material-ui/icons'

import { adminChangeTenant, adminListTenants } from 'components/api'
import { getApiErrorMessage } from 'utils/getApiErrorMessage'
import { cacheTenantNames } from '../tenantNameCache'
import AdminModal from 'components/admin/AdminModal'

const useStyles = makeStyles({
    root: {
        padding: 20,
        minWidth: 360
    },
    select: {
        marginTop: 6
    },
    hint: {
        marginTop: 12,
        color: '#5e5e5e'
    },
    buttonContainer: {
        paddingTop: 20,
        borderTop: '1px solid #dee2e6'
    },
    closeButton: {
        color: '#ef8c25'
    },
    loading: {
        display: 'flex',
        justifyContent: 'center',
        padding: 24
    }
})

function ChangeTenantDialog({ onChanged, onToast, onError, confirmRef }, ref) {
    const classes = useStyles()
    const [open, setOpen] = React.useState(false)
    const [user, setUser] = React.useState(null)
    const [tenants, setTenants] = React.useState([])
    const [loadingTenants, setLoadingTenants] = React.useState(false)
    const [selectedTenantId, setSelectedTenantId] = React.useState('')
    const [loadError, setLoadError] = React.useState(null)

    const currentTenantId = user?.['custom:tenantid'] || ''
    const currentTenantName = user?.tenant_name || currentTenantId || '—'
    const selectedTenant = tenants.find(t => t.tenant_id === selectedTenantId)
    const unchanged = !selectedTenantId || selectedTenantId === currentTenantId

    const close = React.useCallback(() => {
        setOpen(false)
        setUser(null)
        setSelectedTenantId('')
        setLoadError(null)
    }, [])

    const loadTenants = React.useCallback(() => {
        setLoadingTenants(true)
        setLoadError(null)
        return adminListTenants()
            .then(result => {
                const list = result.tenants || []
                setTenants(list)
                cacheTenantNames(list)
            })
            .catch(err => {
                setLoadError(getApiErrorMessage(err, 'Failed to load tenants'))
                setTenants([])
            })
            .finally(() => setLoadingTenants(false))
    }, [])

    React.useImperativeHandle(ref, () => ({
        open: nextUser => {
            setUser(nextUser)
            setSelectedTenantId(nextUser?.['custom:tenantid'] || '')
            setLoadError(null)
            setOpen(true)
            loadTenants()
        }
    }))

    const onSubmit = React.useCallback(() => {
        if (!user || unchanged) return
        const username = user.Username || user.email
        const targetName = selectedTenant?.tenant_name || selectedTenantId

        confirmRef.current.confirm({
            title: 'Move user to another tenant?',
            message: (
                <>
                    Move <strong>{user.given_name} {user.family_name}</strong> from{' '}
                    <strong>{currentTenantName}</strong> to <strong>{targetName}</strong>?
                    <Typography variant="body2" style={{ marginTop: 12 }}>
                        This updates their <code>custom:tenantid</code> claim in Cognito. They will
                        see the new tenant's data on their next sign-in.
                    </Typography>
                </>
            ),
            confirmLabel: 'Move user',
            onConfirm: async () => {
                try {
                    await adminChangeTenant(username, selectedTenantId)
                    if (onToast) onToast(`Moved ${user.email} to ${targetName}`)
                    if (onChanged) await onChanged()
                    close()
                } catch (err) {
                    if (onError) onError(getApiErrorMessage(err, 'Failed to change tenant'))
                    throw err
                }
            }
        })
    }, [
        user,
        unchanged,
        selectedTenantId,
        selectedTenant,
        currentTenantName,
        confirmRef,
        onToast,
        onChanged,
        onError,
        close
    ])

    return (
        <AdminModal open={open} onClose={close} title="Change tenant">
            {!user ? null : loadingTenants ? (
                <div className={classes.loading}>
                    <CircularProgress size={28} />
                </div>
            ) : (
                <>
                    <Grid container className={classes.root} spacing={2}>
                        <Grid item xs={12}>
                            <Typography variant="body2" color="textSecondary">
                                Current tenant
                            </Typography>
                            <Typography variant="subtitle1">{currentTenantName}</Typography>
                        </Grid>
                        {loadError && (
                            <Grid item xs={12}>
                                <Typography color="error" variant="body2">
                                    {loadError}{' '}
                                    <Button size="small" onClick={loadTenants}>Retry</Button>
                                </Typography>
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <FormControl variant="outlined" fullWidth size="small" disabled={!!loadError}>
                                <InputLabel id="change-tenant-select-label">New tenant</InputLabel>
                                <Select
                                    labelId="change-tenant-select-label"
                                    value={selectedTenantId}
                                    onChange={e => setSelectedTenantId(e.target.value)}
                                    label="New tenant"
                                    className={classes.select}
                                >
                                    {tenants.map(t => (
                                        <MenuItem key={t.tenant_id} value={t.tenant_id}>
                                            {t.tenant_name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="body2" className={classes.hint}>
                                Super admins can reassign a user to any tenant. The user's roles are
                                unchanged; only their tenant claim is updated.
                            </Typography>
                        </Grid>
                    </Grid>
                    <DialogActions>
                        <Grid container className={classes.buttonContainer} justify="flex-end" spacing={2}>
                            <Grid item>
                                <Button onClick={close} className={classes.closeButton}>Cancel</Button>
                            </Grid>
                            <Grid item>
                                <Button
                                    onClick={onSubmit}
                                    variant="contained"
                                    color="primary"
                                    startIcon={<MoveIcon />}
                                    disabled={unchanged || !!loadError || tenants.length === 0}
                                >
                                    Move user
                                </Button>
                            </Grid>
                        </Grid>
                    </DialogActions>
                </>
            )}
        </AdminModal>
    )
}

export default React.forwardRef(ChangeTenantDialog)
